import type { CSSProperties } from 'react';

import classNames from 'classnames';

import BlueskyIcon from './components/BlueskyIcon';
import DiscordIcon from './components/DiscordIcon';
import FacebookIcon from './components/FacebookIcon';
import InstagramIcon from './components/InstagramIcon';
import LinkedinIcon from './components/LinkedInIcon';
import RssIcon from './components/RssIcon';
import ThreadsIcon from './components/ThreadsIcon';
import TiktokIcon from './components/TiktokIcon';
import TwitterIcon from './components/TwitterIcon';
import YoutubeIcon from './components/YoutubeIcon';

import { computeAlignment } from '../utils/computeAlignment';
import { prefixUrlWithProtocol } from '../utils/prefixUrlWithProtocol';
import { withAlpha } from '../utils/withAlpha';

import { useDreamComponentsContext } from '../dreamComponentProvider/hooks';
import * as styles from './Socials.css';
import type { SocialsElementProps } from './Socials.types';
import { DEFAULT_SOCIALS_ORDER, type SocialsPlatformKey, socialUrl, validSocialsOrder } from './socialsOrder';

const EMPTY_PREVIEW_KEYS = ['twitter', 'linkedin', 'facebook', 'instagram'] as const;
const EMPTY_FALLBACK_COLOR = '#000000';

type SocialIconProps = {
  url: string;
  ariaLabel: string;
  iconSize?: string;
  className?: string;
  style?: CSSProperties;
  Icon: React.ElementType;
  fill?: string;
  backgroundRingEnabled?: boolean;
  backgroundColor?: string;
};

const IconWrapper = ({
  url,
  ariaLabel,
  className,
  style,
  Icon,
  fill,
  backgroundRingEnabled,
  backgroundColor,
}: SocialIconProps) => {
  return (
    <a
      href={prefixUrlWithProtocol(url)}
      aria-label={ariaLabel}
      target="_blank"
      className={classNames(className, 'relative p-[--icon-padding] flex items-center justify-center')}
      style={{ ...style }}
      rel="noreferrer"
    >
      {backgroundRingEnabled && backgroundColor && (
        <div className="absolute -inset-2 rounded-full" style={{ backgroundColor, opacity: 0.15 }} />
      )}
      <Icon fill={fill} className={classNames(styles.socialIcon, 'relative')} />
    </a>
  );
};

export function SocialsElement(props: SocialsElementProps) {
  const { element, children, attributes, className, isLive, isViewportMobile = false } = props;
  const { isEditorCanvas } = useDreamComponentsContext();
  const {
    data,
    emptyStateColor,
    padding = '10px',
    gap = '16px',
    iconSize = '14px',
    iconPadding = '4px',
    width = '100%',
    maxWidth = '100%',
    flexShrink,
    alignment,
    mobileAlignment,
    mobileGap,
    mobilePadding,

    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    // Social media specific styles
    linkedinFill,
    linkedinBackground,
    linkedinBorderWidth,
    linkedinBorderColor,
    linkedinBorderRadius,
    linkedinBorderStyle,

    twitterFill,
    twitterBackground,
    twitterBorderWidth,
    twitterBorderColor,
    twitterBorderRadius,
    twitterBorderStyle,

    facebookFill,
    facebookBackground,
    facebookBorderWidth,
    facebookBorderColor,
    facebookBorderRadius,
    facebookBorderStyle,

    instagramFill,
    instagramBackground,
    instagramBorderWidth,
    instagramBorderColor,
    instagramBorderRadius,
    instagramBorderStyle,

    tiktokFill,
    tiktokBackground,
    tiktokBorderWidth,
    tiktokBorderColor,
    tiktokBorderRadius,
    tiktokBorderStyle,

    youtubeFill,
    youtubeBackground,
    youtubeBorderWidth,
    youtubeBorderColor,
    youtubeBorderRadius,
    youtubeBorderStyle,

    discordFill,
    discordBackground,
    discordBorderWidth,
    discordBorderColor,
    discordBorderRadius,
    discordBorderStyle,

    threadsFill,
    threadsBackground,
    threadsBorderWidth,
    threadsBorderColor,
    threadsBorderRadius,
    threadsBorderStyle,

    blueskyFill,
    blueskyBackground,
    blueskyBorderWidth,
    blueskyBorderColor,
    blueskyBorderRadius,
    blueskyBorderStyle,

    rssFill,
    rssBackground,
    rssBorderWidth,
    rssBorderColor,
    rssBorderRadius,
    rssBorderStyle,

    backgroundRingEnabled,
  } = element.attrs;
  const platforms: Record<
    SocialsPlatformKey,
    {
      Icon: React.ElementType;
      ariaLabel: string;
      className: string;
      fill?: string;
      background?: string;
      borderWidth?: string;
      borderColor?: string;
      borderRadius?: string;
      borderStyle?: string;
    }
  > = {
    twitter: {
      Icon: TwitterIcon,
      ariaLabel: 'Twitter',
      className: 'bg-[--twitter-background]',
      fill: twitterFill,
      background: twitterBackground,
      borderWidth: twitterBorderWidth,
      borderColor: twitterBorderColor,
      borderRadius: twitterBorderRadius,
      borderStyle: twitterBorderStyle,
    },
    linkedin: {
      Icon: LinkedinIcon,
      ariaLabel: 'LinkedIn',
      className: 'bg-[--linkedin-background]',
      fill: linkedinFill,
      background: linkedinBackground,
      borderWidth: linkedinBorderWidth,
      borderColor: linkedinBorderColor,
      borderRadius: linkedinBorderRadius,
      borderStyle: linkedinBorderStyle,
    },
    facebook: {
      Icon: FacebookIcon,
      ariaLabel: 'Facebook',
      className: 'bg-[--facebook-background]',
      fill: facebookFill,
      background: facebookBackground,
      borderWidth: facebookBorderWidth,
      borderColor: facebookBorderColor,
      borderRadius: facebookBorderRadius,
      borderStyle: facebookBorderStyle,
    },
    instagram: {
      Icon: InstagramIcon,
      ariaLabel: 'Instagram',
      className: 'bg-[--instagram-background]',
      fill: instagramFill,
      background: instagramBackground,
      borderWidth: instagramBorderWidth,
      borderColor: instagramBorderColor,
      borderRadius: instagramBorderRadius,
      borderStyle: instagramBorderStyle,
    },
    tiktok: {
      Icon: TiktokIcon,
      ariaLabel: 'TikTok',
      className: 'bg-[--tiktok-background]',
      fill: tiktokFill,
      background: tiktokBackground,
      borderWidth: tiktokBorderWidth,
      borderColor: tiktokBorderColor,
      borderRadius: tiktokBorderRadius,
      borderStyle: tiktokBorderStyle,
    },
    youtube: {
      Icon: YoutubeIcon,
      ariaLabel: 'YouTube',
      className: 'bg-[--youtube-background]',
      fill: youtubeFill,
      background: youtubeBackground,
      borderWidth: youtubeBorderWidth,
      borderColor: youtubeBorderColor,
      borderRadius: youtubeBorderRadius,
      borderStyle: youtubeBorderStyle,
    },
    discord: {
      Icon: DiscordIcon,
      ariaLabel: 'Discord',
      className: 'bg-[--discord-background]',
      fill: discordFill,
      background: discordBackground,
      borderWidth: discordBorderWidth,
      borderColor: discordBorderColor,
      borderRadius: discordBorderRadius,
      borderStyle: discordBorderStyle,
    },
    threads: {
      Icon: ThreadsIcon,
      ariaLabel: 'Threads',
      className: 'bg-[--threads-background]',
      fill: threadsFill,
      background: threadsBackground,
      borderWidth: threadsBorderWidth,
      borderColor: threadsBorderColor,
      borderRadius: threadsBorderRadius,
      borderStyle: threadsBorderStyle,
    },
    bluesky: {
      Icon: BlueskyIcon,
      ariaLabel: 'Bluesky',
      className: 'bg-[--bluesky-background]',
      fill: blueskyFill,
      background: blueskyBackground,
      borderWidth: blueskyBorderWidth,
      borderColor: blueskyBorderColor,
      borderRadius: blueskyBorderRadius,
      borderStyle: blueskyBorderStyle,
    },
    rss: {
      Icon: RssIcon,
      ariaLabel: 'RSS',
      className: 'bg-[--rss-background]',
      fill: rssFill,
      background: rssBackground,
      borderWidth: rssBorderWidth,
      borderColor: rssBorderColor,
      borderRadius: rssBorderRadius,
      borderStyle: rssBorderStyle,
    },
  };
  const order = validSocialsOrder(element.attrs.order) ?? DEFAULT_SOCIALS_ORDER;
  const hasFilled = order.some((key) => Boolean(socialUrl(data, key)));

  if (!hasFilled) {
    // With no link to show, a published page renders nothing: the outline below invites the links
    // in, which only makes sense on a builder canvas. `data-node-id` alone does not say which is
    // which, because the shared serializer stamps it on the public site too.
    if (!isEditorCanvas || !attributes?.['data-node-id']) return null;

    const emptyBase =
      emptyStateColor ?? EMPTY_PREVIEW_KEYS.map((key) => platforms[key].fill).find(Boolean) ?? EMPTY_FALLBACK_COLOR;

    return (
      <div
        className={classNames(
          className,
          'flex flex-wrap items-center gap-2.5 rounded-md border border-dashed px-2 py-2',
        )}
        style={
          {
            '--icon-size': iconSize,
            color: withAlpha(emptyBase, 0.4),
            borderColor: withAlpha(emptyBase, 0.3),
          } as CSSProperties
        }
        {...attributes}
      >
        {EMPTY_PREVIEW_KEYS.map((key) => {
          const platform = platforms[key];
          const Icon = platform.Icon;
          return (
            <span key={key} className="flex items-center justify-center" aria-hidden>
              <Icon fill="currentColor" className={styles.socialIcon} />
            </span>
          );
        })}
        <span className="text-[10px] leading-none">Add social links</span>
      </div>
    );
  }

  // Add 16px to gap when background ring is enabled to account for the ring overflow (-inset-2 = 8px * 2 sides)
  const ringGapOffset = backgroundRingEnabled ? 16 : 0;
  const computedGap = gap ? `calc(${gap} + ${ringGapOffset}px)` : `${ringGapOffset}px`;
  const computedMobileGap = mobileGap ? `calc(${mobileGap} + ${ringGapOffset}px)` : computedGap;

  const mobileJustifyContent = computeAlignment(mobileAlignment ?? alignment);

  const viewportMobileLayoutStyle: CSSProperties = isViewportMobile
    ? {
        justifyContent: mobileJustifyContent,
        gap: computedMobileGap,
        padding: mobilePadding ?? padding,
        width: '100%',
      }
    : {};

  const hasMobilePadding = mobilePadding != null;

  return (
    <div
      className={classNames(
        styles.socialContainer,
        className,
        'flex flex-wrap items-center w-full relative',
        !isLive ? 'pointer-events-none' : '',
      )}
      data-has-mobile-padding={hasMobilePadding ? 'true' : undefined}
      style={
        {
          padding: hasMobilePadding ? undefined : padding,
          ...(hasMobilePadding && {
            '--padding': padding,
            '--mobile-padding': mobilePadding,
          }),
          width: width,
          maxWidth: maxWidth,
          ...(flexShrink !== undefined && { flexShrink }),
          ...viewportMobileLayoutStyle,

          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--border-radius': borderRadius,
          '--justify-content': computeAlignment(alignment),
          '--mobile-justify-content': mobileJustifyContent,
          '--gap': computedGap,
          '--mobile-gap': computedMobileGap,

          '--icon-size': iconSize,
          '--icon-padding': iconPadding,

          '--linkedin-fill': linkedinFill,
          '--linkedin-background': linkedinBackground,
          '--linkedin-border-width': linkedinBorderWidth,
          '--linkedin-border-color': linkedinBorderColor,
          '--linkedin-border-radius': linkedinBorderRadius,
          '--linkedin-border-style': linkedinBorderStyle,

          '--twitter-fill': twitterFill,
          '--twitter-background': twitterBackground,
          '--twitter-border-width': twitterBorderWidth,
          '--twitter-border-color': twitterBorderColor,
          '--twitter-border-radius': twitterBorderRadius,
          '--twitter-border-style': twitterBorderStyle,

          '--facebook-fill': facebookFill,
          '--facebook-background': facebookBackground,
          '--facebook-border-width': facebookBorderWidth,
          '--facebook-border-color': facebookBorderColor,
          '--facebook-border-radius': facebookBorderRadius,
          '--facebook-border-style': facebookBorderStyle,

          '--instagram-fill': instagramFill,
          '--instagram-background': instagramBackground,
          '--instagram-border-width': instagramBorderWidth,
          '--instagram-border-color': instagramBorderColor,
          '--instagram-border-radius': instagramBorderRadius,
          '--instagram-border-style': instagramBorderStyle,

          '--tiktok-fill': tiktokFill,
          '--tiktok-background': tiktokBackground,
          '--tiktok-border-width': tiktokBorderWidth,
          '--tiktok-border-color': tiktokBorderColor,
          '--tiktok-border-radius': tiktokBorderRadius,
          '--tiktok-border-style': tiktokBorderStyle,

          '--youtube-fill': youtubeFill,
          '--youtube-background': youtubeBackground,
          '--youtube-border-width': youtubeBorderWidth,
          '--youtube-border-color': youtubeBorderColor,
          '--youtube-border-radius': youtubeBorderRadius,
          '--youtube-border-style': youtubeBorderStyle,

          '--discord-fill': discordFill,
          '--discord-background': discordBackground,
          '--discord-border-width': discordBorderWidth,
          '--discord-border-color': discordBorderColor,
          '--discord-border-radius': discordBorderRadius,
          '--discord-border-style': discordBorderStyle,

          '--threads-fill': threadsFill,
          '--threads-background': threadsBackground,
          '--threads-border-width': threadsBorderWidth,
          '--threads-border-color': threadsBorderColor,
          '--threads-border-radius': threadsBorderRadius,
          '--threads-border-style': threadsBorderStyle,

          '--bluesky-fill': blueskyFill,
          '--bluesky-background': blueskyBackground,
          '--bluesky-border-width': blueskyBorderWidth,
          '--bluesky-border-color': blueskyBorderColor,
          '--bluesky-border-radius': blueskyBorderRadius,
          '--bluesky-border-style': blueskyBorderStyle,

          '--rss-fill': rssFill,
          '--rss-background': rssBackground,
          '--rss-border-width': rssBorderWidth,
          '--rss-border-color': rssBorderColor,
          '--rss-border-radius': rssBorderRadius,
          '--rss-border-style': rssBorderStyle,
        } as CSSProperties
      }
      {...attributes}
    >
      {order.map((key) => {
        const url = socialUrl(data, key);
        if (!url) return null;

        const platform = platforms[key];
        return (
          <IconWrapper
            key={key}
            url={url}
            ariaLabel={platform.ariaLabel}
            className={platform.className}
            style={{
              borderColor: platform.borderColor,
              borderWidth: platform.borderWidth,
              borderRadius: platform.borderRadius,
              borderStyle: platform.borderStyle,
            }}
            Icon={platform.Icon}
            fill={platform.fill}
            backgroundRingEnabled={backgroundRingEnabled}
            backgroundColor={platform.background}
          />
        );
      })}
      {children}
    </div>
  );
}
